import { useState, useEffect } from "react";
import { Form, Button, Card, Col, Row, Spinner, Alert } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../services/api";
import Swal from "sweetalert2";
import { carregarCamposFormulario } from "../../utils/formularios";

const FormularioEdicaoReporte = () => {
  const { reporteId } = useParams();
  const navigate = useNavigate();

  const [formFields, setFormFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [reporteOriginal, setReporteOriginal] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const reporteResponse = await api.get(
          `/formularios/respostas/${reporteId}/`
        );
        const reporteData = reporteResponse.data;
        setFormData(reporteData.respostas);
        setReporteOriginal(reporteData);

        const formId = reporteData.formulario_id;
        if (formId) {
          const campos = await carregarCamposFormulario(formId, { status: null, limit: null });
          setFormFields(campos);
        } else {
          setError("ID do formulário não encontrado no reporte.");
        }
      } catch (err) {
        console.error("Erro ao carregar dados para edição:", err);
        setError("Não foi possível carregar os dados para edição.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [reporteId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const payload = {
      respostas: formData,
      formulario_id: reporteOriginal.formulario_id,
      programa_id: reporteOriginal.programa_id,
      prestador_id: reporteOriginal.prestador_id,
    };

    try {
      await api.put(`/formularios/respostas/${reporteId}/`, payload);
      await Swal.fire("Sucesso!", "Seu salvo com sucesso.", "success");
      navigate("/reportes-farmaco");
    } catch (err) {
      const errorData = err.response?.data;
      const errorMessage = errorData
        ? Object.values(errorData).flat().join("\n")
        : "Verifique os dados e tente novamente.";
      Swal.fire("Erro na atualização", errorMessage, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFinalizarAnalise = async () => {
    Swal.fire({
      title: "Você tem certeza?",
      text: "Após finalizar, o status do reporte original será atualizado e esta ação não poderá ser desfeita.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, finalizar análise!",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.post(
            `/formularios/respostas/${reporteId}/finalizar-analise/`
          );
          await Swal.fire(
            "Finalizado!",
            "A análise foi marcada como concluída.",
            "success"
          );
          navigate("/reportes-farmaco");
        } catch (err) {
          Swal.fire("Erro!", "Não foi possível finalizar a análise.", "error");
        }
      }
    });
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  return (
    <Card className="cardDestaque">
      <Card.Header>
        <h4>Editando Reporte de FV (Cópia de Trabalho) #{reporteId}</h4>
      </Card.Header>
      <Card.Body>
        <Alert variant="info">
          Você está editando uma cópia de trabalho. O reporte original enviado
          pelo prestador permanecerá inalterado.
          <br />
          <strong>Programa:</strong> {reporteOriginal?.dados_programa?.nome}
          <br />
          <strong>Prestador Original:</strong>{" "}
          {reporteOriginal?.dados_prestador?.nome}
        </Alert>
        <hr />
        <Form onSubmit={handleSubmit}>
          {formFields.map((field) => (
            <Form.Group as={Row} className="mb-3" key={field.slug}>
              <Form.Label column sm={3}>
                {field.nome}
                {field.obrigatorio && " *"}
              </Form.Label>
              <Col sm={9}>
                <Form.Control
                  type={field.tipo === "data" ? "date" : "text"}
                  as={field.tipo === "textarea" ? "textarea" : "input"}
                  rows={field.tipo === "textarea" ? 5 : undefined}
                  name={field.slug}
                  required={field.obrigatorio}
                  value={formData[field.slug] || ""}
                  onChange={handleInputChange}
                />
              </Col>
            </Form.Group>
          ))}

          <div className="d-flex justify-content-end mt-4">
            <Button variant="success" onClick={handleFinalizarAnalise}>
              Finalizar Análise
            </Button>
            <Button
              variant="secondary"
              onClick={() => navigate("/reportes-farmaco")}
              className="ms-2"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={submitting}
              className="ms-2"
            >
              {submitting ? (
                <Spinner as="span" animation="border" size="sm" />
              ) : (
                "Salvar Alterações"
              )}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default FormularioEdicaoReporte;
